/**
 * @fileoverview CalendarTasksPanel Component
 *
 * Shared calendar + task list component used in multiple contexts:
 * - dropdown: Day scroller with task list + mini calendar at bottom (navbar dropdown)
 * - mobile: Compact day scroller with task list, no calendar (legacy, if needed)
 * - panel: Full calendar + close button (slide-out panel)
 * - sidebar: Full calendar with card header (legacy, no longer used in dashboard)
 *
 * The `variant` prop controls layout and which elements are shown.
 *
 * @component
 */

import React from "react";
import {
  FaPlus,
  FaTimes,
  FaEdit,
  FaSpinner,
  FaChevronLeft,
  FaChevronRight,
} from "react-icons/fa";
import { formatDateET } from "../../utils/dateUtils";
import "../../pages/admin/AdminDashboard.css";

/* ============================================
   MINI CALENDAR (sidebar + panel + dropdown variants)
   ============================================ */

/**
 * MiniCalendar — Monthly calendar grid with task indicators
 *
 * @param {Object} props
 * @param {Date} props.selectedDate - Currently selected date
 * @param {Function} props.onDateSelect - Handler when a date is clicked
 * @param {Object} props.tasksForDate - Map of "YYYY-MM-DD" → task array
 */
const MiniCalendar = ({ selectedDate, onDateSelect, tasksForDate }) => {
  const [currentMonth, setCurrentMonth] = React.useState(new Date());

  const daysInMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth() + 1,
    0,
  ).getDate();

  const firstDayOfMonth = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1,
  ).getDay();

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  /** Navigate to previous month */
  const goToPrevMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1),
    );
  };

  /** Navigate to next month */
  const goToNextMonth = () => {
    setCurrentMonth(
      new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1),
    );
  };

  /** Handle clicking a calendar day */
  const handleDateClick = (day) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    onDateSelect(date);
  };

  /** Check if a day is currently selected */
  const isSelected = (day) => {
    if (!selectedDate) return false;
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    return date.toDateString() === selectedDate.toDateString();
  };

  /** Check if a day is today */
  const isToday = (day) => {
    const date = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    );
    return date.toDateString() === today.toDateString();
  };

  /** Check if a day has tasks */
  const hasTasksOnDay = (day) => {
    const dateStr = new Date(
      currentMonth.getFullYear(),
      currentMonth.getMonth(),
      day,
    )
      .toISOString()
      .split("T")[0];
    return tasksForDate && tasksForDate[dateStr]?.length > 0;
  };

  /** Render all calendar day cells */
  const renderDays = () => {
    const days = [];

    for (let i = 0; i < firstDayOfMonth; i++) {
      days.push(
        <div key={`empty-${i}`} className="admin-calendar-day empty" />,
      );
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(
        <button
          key={day}
          className={`admin-calendar-day 
            ${isToday(day) ? "today" : ""} 
            ${isSelected(day) ? "selected" : ""}
            ${hasTasksOnDay(day) ? "has-tasks" : ""}`}
          onClick={() => handleDateClick(day)}
        >
          {day}
        </button>,
      );
    }

    return days;
  };

  return (
    <div className="admin-calendar">
      <div className="admin-calendar-header">
        <button className="admin-calendar-nav" onClick={goToPrevMonth}>
          <FaChevronLeft />
        </button>
        <span className="admin-calendar-month">
          {monthNames[currentMonth.getMonth()]} {currentMonth.getFullYear()}
        </span>
        <button className="admin-calendar-nav" onClick={goToNextMonth}>
          <FaChevronRight />
        </button>
      </div>
      <div className="admin-calendar-weekdays">
        {dayNames.map((day) => (
          <span key={day} className="admin-calendar-weekday">
            {day}
          </span>
        ))}
      </div>
      <div className="admin-calendar-grid">{renderDays()}</div>
    </div>
  );
};

/* ============================================
   TASK ITEM
   ============================================ */

/**
 * TaskItem — Single task row in the task list
 *
 * @param {Object} props
 * @param {string} props.id - Task record ID
 * @param {string} props.taskName - Task title
 * @param {string} props.projectName - Associated project name
 * @param {string} props.projectRecordId - Associated project record ID
 * @param {string} props.startDate - Task start date
 * @param {string} props.endDate - Task end date
 * @param {string} props.dateCompleted - Date task was completed
 * @param {string} props.status - Task status
 * @param {boolean} props.isToday - Whether task is due today
 * @param {Function} props.onClick - Click handler
 */
const TaskItem = ({
  id,
  taskName,
  projectName,
  projectRecordId,
  startDate,
  endDate,
  dateCompleted,
  status,
  isToday,
  onClick,
}) => {
  /** Determine the date display string */
  const getDateDisplay = () => {
    if (status === "Completed" && dateCompleted) {
      return `${formatDateET(startDate)} → ${formatDateET(dateCompleted)}`;
    }
    if (endDate) {
      return formatDateET(endDate);
    }
    if (startDate) {
      return formatDateET(startDate);
    }
    return "No date";
  };

  return (
    <button
      className={`admin-dash-task-item ${isToday ? "today" : ""} ${
        status === "Completed" ? "completed" : ""
      }`}
      onClick={() =>
        onClick &&
        onClick({
          id,
          taskName,
          projectName,
          projectRecordId,
          startDate,
          endDate,
          dateCompleted,
          status,
        })
      }
    >
      <div className="admin-dash-task-content">
        <span className="admin-dash-task-title">{taskName}</span>
        <span className="admin-dash-task-meta">
          {projectName && (
            <span className="admin-dash-task-project">{projectName}</span>
          )}
          <span className="admin-dash-task-status">{status}</span>
          <span className="admin-dash-task-date-info">{getDateDisplay()}</span>
        </span>
      </div>
      <span className="admin-dash-task-edit-icon">
        <FaEdit />
      </span>
    </button>
  );
};

/* ============================================
   LOADING SPINNER
   ============================================ */

/** Inline loading indicator */
const LoadingSpinner = () => (
  <div className="admin-dash-loading">
    <FaSpinner className="admin-dash-spinner" />
    <span>Loading tasks...</span>
  </div>
);

/* ============================================
   DAY SCROLLER (shared by mobile + dropdown)
   ============================================ */

/**
 * DayScroller — Header + day navigation row with close and new task buttons.
 * Extracted to share between mobile and dropdown variants.
 *
 * @param {Object} props
 * @param {Date} props.selectedDate - Currently selected date
 * @param {Function} props.onDateSelect - Date selection handler
 * @param {Function} props.onCreateTask - Handler for "New Task" button
 * @param {Function} props.onClose - Close handler
 */
const DayScroller = ({ selectedDate, onDateSelect, onCreateTask, onClose }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const isToday = selectedDate.toDateString() === today.toDateString();

  /** Go to previous day */
  const goToPrevDay = () => {
    const prev = new Date(selectedDate);
    prev.setDate(prev.getDate() - 1);
    onDateSelect(prev);
  };

  /** Go to next day */
  const goToNextDay = () => {
    const next = new Date(selectedDate);
    next.setDate(next.getDate() + 1);
    onDateSelect(next);
  };

  /** Jump back to today */
  const goToToday = () => {
    onDateSelect(new Date());
  };

  return (
    <>
      {/* Header row: close + title + new task */}
      <div className="admin-dash-mobile-tasks-header">
        <span className="admin-dash-mobile-tasks-title">Tasks & Calendar</span>
        <button
          className="admin-dash-mobile-tasks-close"
          onClick={onClose}
          aria-label="Close tasks panel"
        >
          <FaTimes />
        </button>
      </div>

      {/* Day navigation: ← date → with Today button */}
      <div className="admin-dash-mobile-tasks-nav">
        <button
          className="admin-dash-mobile-tasks-arrow"
          onClick={goToPrevDay}
          aria-label="Previous day"
        >
          <FaChevronLeft />
        </button>
        <div className="admin-dash-mobile-tasks-date">
          <span className="admin-dash-mobile-tasks-date-text">
            {selectedDate.toLocaleDateString("en-US", {
              weekday: "short",
              month: "short",
              day: "numeric",
            })}
          </span>
          {isToday ? (
            <button className="admin-dash-mobile-tasks-today">Today</button>
          ) : (
            <button
              className="admin-dash-mobile-tasks-nottoday"
              onClick={goToToday}
            >
              Back to Today
            </button>
          )}
        </div>
        <button
          className="admin-dash-mobile-tasks-arrow"
          onClick={goToNextDay}
          aria-label="Next day"
        >
          <FaChevronRight />
        </button>
      </div>
    </>
  );
};

/* ============================================
   MAIN COMPONENT
   ============================================ */

/**
 * CalendarTasksPanel — shared calendar + task list
 *
 * @param {Object} props
 * @param {'dropdown'|'mobile'|'panel'|'sidebar'} [props.variant='sidebar'] - Render context
 * @param {Date} props.selectedDate - Currently selected date
 * @param {Function} props.onDateSelect - Date selection handler
 * @param {Object} props.tasksByDate - Map of date strings to task arrays
 * @param {Array} props.tasksForSelectedDate - Tasks for the selected date
 * @param {boolean} props.tasksLoading - Whether tasks are loading
 * @param {Function} props.onTaskClick - Handler when a task item is clicked
 * @param {Function} props.onCreateTask - Handler for "New Task" button
 * @param {Function} [props.onClose] - Close handler (panel/mobile/dropdown variants)
 */
const CalendarTasksPanel = ({
  variant = "sidebar",
  selectedDate,
  onDateSelect,
  tasksByDate,
  tasksForSelectedDate,
  tasksLoading,
  onTaskClick,
  onCreateTask,
  onClose,
}) => {
  /* ---- Dropdown variant: day scroller + task list + mini calendar at bottom ---- */
  if (variant === "dropdown") {
    return (
      <div className="admin-dash-mobile-tasks">
        <DayScroller
          selectedDate={selectedDate}
          onDateSelect={onDateSelect}
          onCreateTask={onCreateTask}
          onClose={onClose}
        />

        {/* Task list */}
        <div className="admin-dash-mobile-tasks-list">
          <div className="admin-dash-mobile-tasks-btn">
            <button
              className="admin-dash-mobile-tasks-add"
              onClick={onCreateTask}
            >
              <FaPlus /> New Task
            </button>
          </div>
          {tasksLoading ? (
            <LoadingSpinner />
          ) : tasksForSelectedDate.length === 0 ? (
            <p className="admin-dash-empty">No tasks for this date</p>
          ) : (
            tasksForSelectedDate.map((task) => (
              <TaskItem
                key={task.id}
                {...task}
                isToday={
                  task.dueDate === new Date().toISOString().split("T")[0]
                }
                onClick={onTaskClick}
              />
            ))
          )}
        </div>

        {/* Mini calendar at bottom */}
        <div className="admin-dash-dropdown-calendar">
          <MiniCalendar
            selectedDate={selectedDate}
            onDateSelect={onDateSelect}
            tasksForDate={tasksByDate}
          />
        </div>
      </div>
    );
  }

  /* ---- Mobile variant: compact day-scroller (no calendar) ---- */
  if (variant === "mobile") {
    return (
      <div className="admin-dash-mobile-tasks">
        <DayScroller
          selectedDate={selectedDate}
          onDateSelect={onDateSelect}
          onCreateTask={onCreateTask}
          onClose={onClose}
        />

        {/* Task list */}
        <div className="admin-dash-mobile-tasks-list">
          {tasksLoading ? (
            <LoadingSpinner />
          ) : tasksForSelectedDate.length === 0 ? (
            <div className="admin-dash-empty">No tasks for this date</div>
          ) : (
            tasksForSelectedDate.map((task) => (
              <TaskItem
                key={task.id}
                {...task}
                isToday={
                  task.dueDate === new Date().toISOString().split("T")[0]
                }
                onClick={onTaskClick}
              />
            ))
          )}
        </div>
      </div>
    );
  }
};

export default CalendarTasksPanel;
